
import React, { useState } from 'react';
import { Comment, User, Theme } from '../types';

interface CommentSectionProps {
  comments: Comment[];
  onAddComment: (text: string) => void;
  currentUser: User;
  theme: Theme;
}

const CommentSection: React.FC<CommentSectionProps> = ({ comments, onAddComment, currentUser, theme }) => {
  const [newComment, setNewComment] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    onAddComment(newComment);
    setNewComment('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xs text-accent font-bold uppercase tracking-widest">Team Internal Notes</h3>
        <span className="text-[10px] text-slate-400 dark:text-white/20">{comments.length} comments</span>
      </div>

      <div className="space-y-4">
        {comments.map(comment => (
          <div key={comment.id} className={`p-4 rounded-xl border space-y-2 transition-colors ${
            theme === 'dark' ? 'bg-white/5 border-white/5' : 'bg-white border-black/5 shadow-sm'
          }`}>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-bold text-accent opacity-80">{comment.authorEmail}</span>
              <span className="text-[9px] text-slate-400 dark:text-white/20">{new Date(comment.timestamp).toLocaleString()}</span>
            </div>
            <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-white/70' : 'text-slate-600'}`}>{comment.text}</p>
          </div>
        ))}
        {comments.length === 0 && (
          <p className="text-center text-xs text-slate-400 dark:text-white/20 py-4 italic">No notes yet. Start the conversation.</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="relative mt-4">
        <textarea
          placeholder="Add an internal note..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className={`w-full border rounded-xl p-3 text-xs focus:outline-none focus:ring-1 focus:ring-accent resize-none h-24 placeholder:text-slate-400 dark:placeholder:text-white/20 transition-all ${
            theme === 'dark' ? 'bg-black/60 border-white/10 text-white' : 'bg-white border-black/5 text-slate-800 shadow-inner'
          }`}
        ></textarea>
        <button 
          type="submit"
          disabled={!newComment.trim()}
          className="absolute bottom-3 right-3 p-2 bg-accent text-black rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-accent/80 transition-all shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
        </button>
      </form>
      <p className="text-[9px] text-slate-400 dark:text-white/20 italic text-center">Comments are stored in the platform database and will NOT be written to Google Sheets.</p>
    </div>
  );
};

export default CommentSection;
